import React, { useEffect, useRef, useState, useMemo } from "react";
import { Download, Info, UploadCloud, Loader2 } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useEntityTypes } from "@features/onboarding/utility-setup/hooks";
import { getColumnsForEntityType } from "../entityConfig";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { useToast } from "@shared/hooks/use-toast";
import { onboardingApi } from "@features/onboarding/utility-setup/api";
import {
  useRemoteUtilityId,
  useTenantId,
} from "@shared/selectors/globalSelectors";

interface ConfigureStepProps {
  selectedEntity: string;
  setSelectedEntity: (entityId: string) => void;
  onFileValidated: (
    file: File,
    validationResponse: { dataUploadId: number; result: any[] },
    onNext?: () => void
  ) => void;
  onCancel: () => void;
  onNext?: () => void;
}

export const ConfigureStep: React.FC<ConfigureStepProps> = ({
  selectedEntity,
  setSelectedEntity,
  onFileValidated,
  onCancel,
  onNext,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const tenantID = useTenantId();
  const remoteUtilityId = useRemoteUtilityId();

  // Fetch real entity types from API hook
  const { data: entityTypesData, isLoading: isLoadingEntityTypes } =
    useEntityTypes();

  const entityTypeOptions = useMemo(() => {
    return (
      entityTypesData?.result?.map((type: any) => ({
        label: type.value,
        value: type.key.toString(),
        id: type.key,
      })) || []
    );
  }, [entityTypesData]);

  // Set default selected entity when options load if none selected
  useEffect(() => {
    if (!selectedEntity && entityTypeOptions.length > 0) {
      setSelectedEntity(entityTypeOptions[0].value.toString());
    }
  }, [entityTypeOptions, selectedEntity, setSelectedEntity]);

  const selectedEntityOption = useMemo(() => {
    return entityTypeOptions.find(
      (e: any) => String(e.value) === String(selectedEntity)
    );
  }, [entityTypeOptions, selectedEntity]);

  const selectedEntityLabel = selectedEntityOption?.label || "Region";

  // Real template download API call
  const handleDownloadTemplate = async (entityKeyToDownload?: string) => {
    const targetKey = entityKeyToDownload || selectedEntity;
    if (!targetKey) {
      toast({
        title: "Selection Required",
        description: "Please select an entity type first.",
        variant: "destructive",
      });
      return;
    }

    const matchedOpt = entityTypeOptions.find(
      (e: any) => String(e.value) === String(targetKey)
    );
    const labelForFile = matchedOpt?.label || selectedEntityLabel || "Territory";
    const entityTypeToSend = matchedOpt?.label || matchedOpt?.value || targetKey;

    setIsDownloading(true);
    try {
      const blob = await onboardingApi.downloadTemplate(
        "Territory",
        entityTypeToSend
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${labelForFile}_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Template Downloaded",
        description: `${labelForFile} CSV template downloaded successfully.`,
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Real file validation API call
  const handleFileProcess = async (file: File, currentEntityKey?: string) => {
    setErrorMessage(null);
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setErrorMessage("Only .csv files are supported.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("File size exceeds maximum 5 MB limit.");
      return;
    }

    const targetKey = currentEntityKey || selectedEntity;

    if (!targetKey || !remoteUtilityId) {
      toast({
        title: "Error",
        description: "Please select entity type first.",
        variant: "destructive",
      });
      return;
    }

    setIsValidating(true);
    try {
      const response = await onboardingApi.validateData(
        Number(targetKey),
        remoteUtilityId,
        tenantID,
        file
      );

      toast({
        title: "File Validated",
        description: `Successfully processed ${response?.result?.length || 0} row(s).`,
      });

      onFileValidated(
        file,
        {
          dataUploadId: response.dataUploadId,
          result: response.result || [],
        },
        onNext
      );
    } catch (error: any) {
      console.error("Validation error:", error);
      toast({
        title: "Validation Failed",
        description:
          "Failed to validate file. Please ensure correct CSV structure.",
        variant: "destructive",
      });
      setErrorMessage("Failed to validate CSV file.");
    } finally {
      setIsValidating(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent, activeEntityKey?: string) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileProcess(e.dataTransfer.files[0], activeEntityKey);
    }
  };

  // Form definition using FormService & FormField
  const fields: FormField[] = [
    {
      name: "entityType",
      label: "Entity Type",
      type: "select",
      required: true,
      options: entityTypeOptions,
      placeholder: isLoadingEntityTypes
        ? "Loading entity types..."
        : "Select entity type",
      disabled: isLoadingEntityTypes,
      helperText: "Select the type of entity you want to import in bulk.",
      className: "col-span-12",
    },
    {
      name: "templateInfo",
      label: "",
      type: "custom",
      className: "col-span-12",
      customRender: (_field: FormField, formMethod: any) => {
        const currentEntityValue =
          formMethod?.watch ? formMethod.watch("entityType") : selectedEntity;

        const activeOption = entityTypeOptions.find(
          (e: any) => String(e.value) === String(currentEntityValue || selectedEntity)
        );

        const activeLabel = activeOption?.label || selectedEntityLabel || "Region";
        const activeColumns = getColumnsForEntityType(activeLabel);

        return (
          <div className="space-y-4 my-2">
            {/* Template Columns Panel dynamically re-rendering on selection */}
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50 space-y-3">
              <p className="text-xs font-semibold text-gray-600">
                Template Columns
              </p>
              <div className="flex flex-wrap gap-2">
                {activeColumns.map((col) => (
                  <Badge
                    key={col.key}
                    variant="outline"
                    className="bg-white border-gray-300 text-gray-800 px-3 py-1 text-xs font-medium shadow-2xs"
                  >
                    {col.label}
                    {col.required && (
                      <span className="text-red-500 font-bold ml-1">*</span>
                    )}
                  </Badge>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 italic">
                <span className="text-red-500 font-bold">*</span> Required fields
              </p>
            </div>

            {/* Info Banner */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0" />
              <p className="text-xs text-blue-700 font-medium">
                Accepted formats: <span className="font-bold">.csv</span>. Max
                file size: <span className="font-bold">5 MB</span>. Download the
                template to ensure correct column headers.
              </p>
            </div>

            {/* Download CSV Template Button */}
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleDownloadTemplate(currentEntityValue)}
                disabled={isDownloading || !currentEntityValue}
                className="border-gray-300 font-medium text-gray-700 hover:bg-gray-50"
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download CSV Template
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      name: "uploadFile",
      label: "Upload File",
      type: "custom",
      className: "col-span-12",
      customRender: (_field: FormField, formMethod: any) => {
        const currentEntityValue =
          formMethod?.watch ? formMethod.watch("entityType") : selectedEntity;

        return (
          <div className="space-y-2">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, currentEntityValue)}
              onClick={() => !isValidating && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center ${
                isValidating
                  ? "border-blue-300 bg-blue-50/30 opacity-70 cursor-wait"
                  : isDragging
                    ? "border-blue-500 bg-blue-50/50"
                    : "border-gray-300 hover:border-gray-400 bg-white"
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                className="hidden"
                disabled={isValidating}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileProcess(e.target.files[0], currentEntityValue);
                  }
                }}
              />
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                {isValidating ? (
                  <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                ) : (
                  <UploadCloud className="h-6 w-6 text-gray-500" />
                )}
              </div>
              <p className="text-sm font-bold text-gray-800">
                {isValidating
                  ? "Validating CSV Data..."
                  : "Drag & drop your file here"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {isValidating
                  ? "Please wait while we check records against database rules"
                  : "or click to browse from your computer"}
              </p>
            </div>
            {errorMessage && (
              <p className="text-xs text-red-600 font-medium mt-1">
                {errorMessage}
              </p>
            )}
          </div>
        );
      },
    },
  ];

  const form = FormService.createForm({
    fields,
    defaultValues: {
      entityType: selectedEntity || "",
    },
  });

  const watchedEntityType = form.watch("entityType");

  useEffect(() => {
    if (
      watchedEntityType &&
      String(watchedEntityType) !== String(selectedEntity)
    ) {
      setSelectedEntity(String(watchedEntityType));
    }
  }, [watchedEntityType, selectedEntity, setSelectedEntity]);

  useEffect(() => {
    if (
      selectedEntity &&
      String(form.getValues("entityType")) !== String(selectedEntity)
    ) {
      form.setValue("entityType", selectedEntity);
    }
  }, [selectedEntity, form]);

  return (
    <div className="space-y-6">
      <DynamicForm
        form={form}
        fields={fields}
        onSubmit={() => {}}
        onReset={onCancel}
        loading={false}
        gridColumns={12}
        responsiveColumns={{ sm: 12, md: 12 }}
        config={{
          layout: "vertical",
          showSubmitButton: false,
          showResetButton: false,
          classes: BUTTON_FIELD_CLASSES,
        }}
      />

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
        <Button variant="outline" onClick={onCancel} disabled={isValidating}>
          Cancel
        </Button>
        <span className="text-xs text-gray-400 font-medium">
          Upload a file to continue
        </span>
      </div>
    </div>
  );
};
